/**
 * ImportDataPage — dedicated Import Data page (replaces the Bulk Import modal).
 * Sections: Organization Structure (recommended first) + Members.
 * Flow per section: Upload & Validate → Review → Results, with a step indicator
 * shown from the Review step onward. Members review is an always-present record
 * list (read-only clean rows, inline-editable rows with errors/warnings); org
 * review is a stats + issues summary. Plus an Imports History list, a contextual
 * live-undo banner, and undo via a batch summary.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { FileDropZone } from '../components/ui/FileDropZone';
import { MemberReviewPanel } from '../components/imports/MemberReviewPanel';
import { useToast } from '../hooks/useToast';
import { handleApiError } from '../lib/axios';
import { teamsService } from '../services/teams.service';
import { departmentsService } from '../services/departments.service';
import {
  importsService,
  type OrgValidationResponse, type OrgImportResponse,
  type MemberValidationResponse, type MemberImportResponse,
  type RowError, type RowWarning, type CorrectedRow, type ImportBatch,
} from '../services/imports.service';
import {
  Building2, Users, Download, Upload, Check, CheckCircle, XCircle, AlertTriangle, ArrowLeft, RotateCcw, History,
} from 'lucide-react';

type ImportType = 'org' | 'members';
type Step = 'select' | 'review' | 'results';

const CSV_MAX = 5 * 1024 * 1024;
const ZIP_MAX = 500 * 1024 * 1024;
const CACHE_KEYS_ORG = ['departments', 'departments-all', 'teams', 'teams-all', 'dashboardOverview', 'dashboardStats'];
const CACHE_KEYS_MEMBERS = ['members', ...CACHE_KEYS_ORG];

export const ImportDataPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState<ImportType>('org');
  const [step, setStep] = useState<Step>('select');
  const [orgFiles, setOrgFiles] = useState<{ csv: File | null; zip: File | null }>({ csv: null, zip: null });
  const [memberFiles, setMemberFiles] = useState<{ csv: File | null; zip: File | null }>({ csv: null, zip: null });
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [orgValidation, setOrgValidation] = useState<OrgValidationResponse | null>(null);
  const [memberValidation, setMemberValidation] = useState<MemberValidationResponse | null>(null);
  const [orgResult, setOrgResult] = useState<OrgImportResponse | null>(null);
  const [memberResult, setMemberResult] = useState<MemberImportResponse | null>(null);
  const [viewBatch, setViewBatch] = useState<ImportBatch | null>(null);

  const batchesQuery = useQuery({ queryKey: ['import-batches'], queryFn: importsService.listBatches });
  const deptQuery = useQuery({ queryKey: ['departments-all'], queryFn: departmentsService.getAll });
  const teamQuery = useQuery({ queryKey: ['teams-all'], queryFn: teamsService.getAll });
  const deptNames = useMemo(() => (deptQuery.data ?? []).map((d) => d.name), [deptQuery.data]);
  const teamNames = useMemo(() => (teamQuery.data ?? []).map((t) => t.name), [teamQuery.data]);

  const invalidate = (keys: string[]) => {
    keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
    queryClient.invalidateQueries({ queryKey: ['import-batches'] });
  };
  const resetFlow = () => {
    setStep('select'); setUploadId(null);
    setOrgValidation(null); setMemberValidation(null);
    setOrgResult(null); setMemberResult(null); setViewBatch(null);
  };

  const uploadValidate = useMutation({
    mutationFn: async (t: ImportType) => {
      const files = t === 'org' ? orgFiles : memberFiles;
      if (!files.csv) throw new Error('CSV file is required');
      if (t === 'org') {
        const up = await importsService.uploadOrg(files.csv, files.zip);
        return { t, uploadId: up.upload_id, org: await importsService.validateOrg(up.upload_id) } as const;
      }
      const up = await importsService.uploadMembers(files.csv, files.zip);
      return { t, uploadId: up.upload_id, member: await importsService.validateMembers(up.upload_id) } as const;
    },
    onSuccess: (r) => {
      setType(r.t); setUploadId(r.uploadId);
      if (r.t === 'org') setOrgValidation(r.org!); else setMemberValidation(r.member!);
      setStep('review');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Upload/validation failed'),
  });

  const execute = useMutation({
    mutationFn: async (rows?: CorrectedRow[]) => {
      if (!uploadId) throw new Error('No upload');
      if (type === 'org') return { t: 'org' as const, org: await importsService.executeOrg(uploadId) };
      return { t: 'members' as const, member: await importsService.executeMembers(uploadId, rows) };
    },
    onSuccess: (r) => {
      if (r.t === 'org') { setOrgResult(r.org!); invalidate(CACHE_KEYS_ORG); }
      else { setMemberResult(r.member!); invalidate(CACHE_KEYS_MEMBERS); }
      setStep('results');
      toast.success('Import complete');
    },
    // On a 422 all-or-nothing abort (TW-229 — blocking rows, nothing imported) the
    // backend message is surfaced and we stay on Review so the admin can fix the
    // flagged rows inline and retry. handleApiError reads response.data.error.
    onError: (e: unknown) => toast.error(handleApiError(e)),
  });

  const tmpl = useMutation({
    mutationFn: (t: ImportType) => (t === 'org' ? importsService.downloadOrgTemplate() : importsService.downloadMemberTemplate()),
    onError: () => toast.error('Failed to download template'),
  });
  const downloadCurrent = useMutation({
    mutationFn: () => importsService.downloadCurrentStructure(),
    onError: () => toast.error('Failed to export current structure'),
  });

  const busy = uploadValidate.isPending || execute.isPending;
  const liveUndoBatch = (batchesQuery.data ?? []).find((b) => b.type === 'members' && b.undoable) ?? null;

  return (
    <Layout>
      <div className="space-y-10">
        <div className="flex items-center gap-3">
          {(step !== 'select' || viewBatch) && (
            <button onClick={resetFlow} className="text-meta hover:text-heading" aria-label="Back">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-heading flex items-center">
              <svg className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import Data
            </h1>
          </div>
        </div>

        {viewBatch && (
          <BatchSummary batch={viewBatch} onBack={resetFlow} onUndone={() => { invalidate(CACHE_KEYS_MEMBERS); resetFlow(); }} />
        )}

        {!viewBatch && step === 'select' && (
          <>
            {liveUndoBatch && (
              <div className="flex items-center justify-between rounded-lg border border-orange-400 bg-surface-app px-4 py-3">
                <span className="text-sm font-bold text-orange-700 dark:text-orange-300">
                  ↩ A recent member import can still be undone ({liveUndoBatch.imported_count} imported).
                </span>
                <Button variant="secondary" onClick={() => setViewBatch(liveUndoBatch)}>Review</Button>
              </div>
            )}

            <ImportCard
              icon={<Building2 className="h-5 w-5" />} title="Organization Structure"
              instructions={
                <ul className="text-sm text-meta space-y-1 list-disc list-inside">
                  <li>Download the CSV template and fill in department and team data (department_name, team_name).</li>
                  <li>Keep the header row in CSV template</li>
                  <li>Save the file as <b>CSV UTF-8</b> — in Excel: File → Save As → &quot;CSV UTF-8 (Comma delimited)&quot;. It keeps letters such as č, ć, đ, š, ž exactly as typed.</li>
                  <li>One row per team; leave team_name blank for a department, or department_name blank for a standalone team.</li>
                  <li>Departments and teams are matched by name — existing update, new are created.</li>
                  <li>Proof your CSV file in text editor</li>
                  <li>Optionally, create a ZIP file with department/team logos (named: department_or_team_name.png; up to 2,000 images)</li>
                  <li>💡 Recommended: import structure before members so members can be assigned to departments and teams.</li>
                </ul>
              }
              files={orgFiles} setFiles={setOrgFiles} disabled={busy}
              onImport={() => { setType('org'); uploadValidate.mutate('org'); }} importing={uploadValidate.isPending && type === 'org'}
              extraActions={<>
                <Button variant="secondary" onClick={() => tmpl.mutate('org')} isLoading={tmpl.isPending}><Download className="h-4 w-4 mr-1.5 text-blue-600" /> Download Template</Button>
                <Button variant="secondary" onClick={() => downloadCurrent.mutate()} isLoading={downloadCurrent.isPending}><Download className="h-4 w-4 mr-1.5 text-blue-600" /> Download Current Structure</Button>
              </>}
              importLabel="Import Organization Structure"
            />

            <ImportCard
              icon={<Users className="h-5 w-5" />} title="Members"
              instructions={
                <ul className="text-sm text-meta space-y-1 list-disc list-inside">
                  <li>Download the CSV template and fill in member data (firstname, lastname, email, gender are required).</li>
                  <li>Keep the header row in CSV template</li>
                  <li>Save the file as <b>CSV UTF-8</b> — in Excel: File → Save As → &quot;CSV UTF-8 (Comma delimited)&quot;. It keeps letters such as č, ć, đ, š, ž exactly as typed.</li>
                  <li>Use "male" or "female" for gender. For dates, format the date columns as text and use YYYY-MM-DD format.</li>
                  <li>Spreadsheets reformat dates on export, proof your CSV file in text editor</li>
                  <li>Optionally, create a ZIP file with member photos (named: firstname_lastname.jpg; up to 2,000 photos, 10 MB each)</li>
                </ul>
              }
              files={memberFiles} setFiles={setMemberFiles} disabled={busy}
              onImport={() => { setType('members'); uploadValidate.mutate('members'); }} importing={uploadValidate.isPending && type === 'members'}
              extraActions={<Button variant="secondary" onClick={() => tmpl.mutate('members')} isLoading={tmpl.isPending}><Download className="h-4 w-4 mr-1.5 text-blue-600" /> Download Template</Button>}
              importLabel="Import Members"
            />

            <ImportsHistory batches={batchesQuery.data ?? []} onReview={(b) => setViewBatch(b)} />
          </>
        )}

        {!viewBatch && step === 'review' && (
          <div className="space-y-6">
            <Stepper activeIndex={1} />
            {/* TW-395: the file wasn't UTF-8 and was converted — ask for a check of č/ć/đ */}
            {(() => {
              const notice = type === 'members' ? memberValidation?.encoding_notice : orgValidation?.encoding_notice;
              return notice ? (
                <div role="alert" className="flex items-start gap-2 rounded-lg border border-orange-400 bg-surface-app px-4 py-3">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-orange-600 dark:text-orange-400" />
                  <span className="text-sm text-orange-700 dark:text-orange-300">{notice}</span>
                </div>
              ) : null;
            })()}
            {type === 'members' && memberValidation ? (
              <MemberReviewPanel
                validation={memberValidation} deptNames={deptNames} teamNames={teamNames}
                onCancel={resetFlow} onImport={(rows) => execute.mutate(rows)} importing={execute.isPending}
              />
            ) : (
              <ReviewPanel type={type} org={orgValidation} member={memberValidation}
                onCancel={resetFlow} onImport={() => execute.mutate(undefined)} importing={execute.isPending} />
            )}
          </div>
        )}

        {!viewBatch && step === 'results' && (
          <div className="space-y-6">
            <Stepper activeIndex={2} />
            <ResultsPanel type={type} org={orgResult} member={memberResult} onDone={resetFlow}
              onInvalidateMembers={() => invalidate(CACHE_KEYS_MEMBERS)} />
          </div>
        )}
      </div>
    </Layout>
  );
};

// ---------- Step indicator (shown from Review onward) ----------
const WIZARD_STEPS = ['Upload', 'Review', 'Done'];
const Stepper = ({ activeIndex }: { activeIndex: number }) => (
  <div className="mx-auto flex w-full max-w-lg items-center">
    {WIZARD_STEPS.map((label, i) => {
      const complete = i < activeIndex;
      const active = i === activeIndex;
      return (
        <Fragment key={label}>
          <div className="flex flex-col items-center">
            <span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
              complete ? 'bg-green-600 text-white'
                : active ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'}`}>
              {complete ? <Check className="h-5 w-5" /> : i + 1}
            </span>
            <span className={`mt-1.5 text-xs ${active ? 'font-medium text-blue-600' : 'text-meta'}`}>{label}</span>
          </div>
          {i < WIZARD_STEPS.length - 1 && (
            <div className={`mx-2 h-0.5 flex-1 ${i < activeIndex ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`} />
          )}
        </Fragment>
      );
    })}
  </div>
);

// ---------- Import card ----------
interface CardProps {
  icon: ReactNode; title: string; description?: string; instructions?: ReactNode;
  files: { csv: File | null; zip: File | null }; setFiles: (f: { csv: File | null; zip: File | null }) => void;
  disabled: boolean; onImport: () => void; importing: boolean; extraActions: ReactNode; helper?: string; importLabel: string;
}
const ImportCard = (p: CardProps) => (
  <div className="bg-surface rounded-xl p-5 space-y-4">
    <div className="flex items-center gap-2">
      <span className="text-blue-600">{p.icon}</span>
      <h2 className="text-lg font-semibold text-heading">{p.title}</h2>
    </div>
    {p.instructions && <div className="pb-2">{p.instructions}</div>}
    {p.description && <p className="text-sm text-body">{p.description}</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FileDropZone label="CSV File (required)" acceptedTypes={['.csv']} maxSize={CSV_MAX}
        onFileSelect={(f) => p.setFiles({ ...p.files, csv: f })} onFileRemove={() => p.setFiles({ ...p.files, csv: null })}
        selectedFile={p.files.csv} disabled={p.disabled} />
      <FileDropZone label="ZIP (optional)" acceptedTypes={['.zip']} maxSize={ZIP_MAX}
        onFileSelect={(f) => p.setFiles({ ...p.files, zip: f })} onFileRemove={() => p.setFiles({ ...p.files, zip: null })}
        selectedFile={p.files.zip} disabled={p.disabled} />
    </div>
    <div className="flex flex-wrap items-center gap-3">
      {p.extraActions}
      <div className="flex-1" />
      <Button onClick={p.onImport} disabled={!p.files.csv || p.disabled} isLoading={p.importing}>
        <Upload className="h-4 w-4 mr-1.5" /> {p.importLabel}
      </Button>
    </div>
    {p.helper && <p className="text-xs text-meta">💡 {p.helper}</p>}
  </div>
);

// ---------- Review (read-only preview) ----------
const ReviewPanel = ({ type, org, member, onCancel, onImport, importing }: {
  type: ImportType; org: OrgValidationResponse | null; member: MemberValidationResponse | null;
  onCancel: () => void; onImport: () => void; importing: boolean;
}) => {
  const errors: RowError[] = type === 'org' ? (org?.errors ?? []) : (member?.errors ?? []);
  const warnings: RowWarning[] = type === 'org' ? (org?.warnings ?? []) : (member?.warnings ?? []);
  const total = type === 'org' ? (org?.total_rows ?? 0) : (member?.total_rows ?? 0);
  const canImport = type === 'org' ? !!org?.can_import : !!member?.can_import;
  return (
    <div className="bg-surface rounded-xl p-5 space-y-4">
      <h2 className="text-lg font-semibold text-heading">Review — {type === 'org' ? 'Organization Structure' : 'Members'}</h2>
      <div className="flex gap-4">
        <Stat label="Rows" value={total} />
        <Stat label="Errors" value={errors.length} tone={errors.length ? 'danger' : 'ok'} />
        <Stat label="Warnings" value={warnings.length} tone={warnings.length ? 'warn' : 'ok'} />
      </div>
      {errors.length > 0 && <IssueList title={`${errors.length} blocking error(s)`} tone="danger" items={errors.map((e) => `Row ${e.row}: ${e.message}`)} />}
      {warnings.length > 0 && <IssueList title={`${warnings.length} warning(s)`} tone="warn" items={warnings.map((w) => `Row ${w.row}: ${w.message}`)} />}
      {!canImport && <p className="text-sm text-status-danger">Fix the blocking errors (edit the CSV and re-upload) before importing.</p>}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button onClick={onImport} disabled={!canImport} isLoading={importing}>Start Import</Button>
      </div>
    </div>
  );
};

// ---------- Results (post-import summary) ----------
const ResultsPanel = ({ type, org, member, onDone, onInvalidateMembers }: {
  type: ImportType; org: OrgImportResponse | null; member: MemberImportResponse | null; onDone: () => void; onInvalidateMembers: () => void;
}) => {
  const toast = useToast();
  const [undone, setUndone] = useState(false);
  const undo = useMutation({
    mutationFn: () => importsService.undo(member!.import_batch_id),
    onSuccess: (r) => { setUndone(true); onInvalidateMembers(); toast.success(`Undone — ${r.deleted} members removed`); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Undo failed'),
  });
  return (
    <div className="bg-surface rounded-xl px-6 py-12 space-y-8 text-center">
      <div className="space-y-4">
        <CheckCircle className="h-14 w-14 text-green-600 mx-auto" />
        <h2 className="text-xl font-bold text-heading">Import complete</h2>
      </div>
      {type === 'org' && org && (
        <p className="text-body pt-4">
          {org.departments_created} departments · {org.teams_created} teams
          {org.teams_reparented > 0 && ` · ${org.teams_reparented} re-parented`}
          {org.logos_applied > 0 && ` · ${org.logos_applied} logos`}
          {org.skipped > 0 && ` · ${org.skipped} skipped`}
        </p>
      )}
      {type === 'members' && member && (
        <>
          <p className="text-body pt-4">{member.imported} imported · {member.skipped} skipped{member.failed > 0 && ` · ${member.failed} failed`}</p>
          <div className="flex justify-center gap-3">
            <Button variant="secondary" onClick={() => importsService.downloadReport(member.report_id)}><Download className="h-4 w-4 mr-1.5 text-blue-600" /> Download report</Button>
            {!undone && member.imported > 0 && (
              <Button variant="danger" onClick={() => undo.mutate()} isLoading={undo.isPending}><RotateCcw className="h-4 w-4 mr-1.5" /> Undo this import</Button>
            )}
          </div>
          {undone && <p className="text-sm text-meta">This import was undone.</p>}
        </>
      )}
      <div className="pt-4"><Button onClick={onDone}>Done</Button></div>
    </div>
  );
};

// ---------- Batch summary (undo entry from history/banner) ----------
const BatchSummary = ({ batch, onBack, onUndone }: { batch: ImportBatch; onBack: () => void; onUndone: () => void }) => {
  const toast = useToast();
  const undo = useMutation({
    mutationFn: () => importsService.undo(batch.id),
    onSuccess: (r) => { toast.success(`Undone — ${r.deleted} members removed`); onUndone(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Undo failed'),
  });
  const label = batch.type === 'members' ? 'Member import' : 'Organization structure import';

  const membersQuery = useQuery({
    queryKey: ['batch-members', batch.id],
    queryFn: () => importsService.batchMembers(batch.id),
    enabled: batch.type === 'members' && !batch.reversed_at,
  });
  const members = membersQuery.data?.members ?? [];

  return (
    <div className="bg-surface rounded-xl p-6 space-y-4">
      <h2 className="text-lg font-semibold text-heading">{label}</h2>
      <p className="text-body text-sm">
        {batch.imported_count} imported{batch.updated_count > 0 && ` · ${batch.updated_count} updated`}{batch.skipped_count > 0 && ` · ${batch.skipped_count} skipped`} · {new Date(batch.created_at).toLocaleString()}{batch.created_by_name && ` · by ${batch.created_by_name}`}
      </p>

      {batch.type === 'members' && !batch.reversed_at && (
        membersQuery.isLoading ? (
          <p className="text-sm text-meta">Loading records…</p>
        ) : members.length > 0 ? (
          <div>
            <p className="text-xs text-meta mb-1">{members.length} member{members.length !== 1 ? 's' : ''} created by this import</p>
            <div className="max-h-72 overflow-y-auto rounded-lg divide-y divide-default">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="w-40 shrink-0 truncate text-body">{[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}</span>
                  <span className="flex-1 truncate text-meta">{m.email}</span>
                  <span className="hidden md:block w-48 shrink-0 truncate text-meta">{[m.department_name, m.team_name].filter(Boolean).join(' · ') || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-meta">No records to show for this import.</p>
        )
      )}

      {batch.type === 'members' && batch.undoable ? (
        <div className="flex gap-3">
          <Button variant="danger" onClick={() => undo.mutate()} isLoading={undo.isPending}><RotateCcw className="h-4 w-4 mr-1.5" /> Undo this import</Button>
          <Button variant="secondary" onClick={onBack}>Back</Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-meta">{batch.reversed_at ? 'This import was undone.' : (batch.undo_reason || 'Undo is not available for this import.')}</p>
          <Button variant="secondary" onClick={onBack}>Back</Button>
        </div>
      )}
    </div>
  );
};

// ---------- Imports history list ----------
const ImportsHistory = ({ batches, onReview }: { batches: ImportBatch[]; onReview: (b: ImportBatch) => void }) => {
  if (batches.length === 0) return null;
  return (
    <div className="bg-surface rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-meta" />
        <h2 className="text-sm font-semibold text-heading">Imports History</h2>
      </div>
      <ul className="divide-y divide-default text-sm">
        {batches.map((b) => (
          <li key={b.id} className="py-2 flex items-center justify-between">
            <div>
              <span className="font-medium text-body">{b.type === 'members' ? 'Members' : 'Organization structure'}</span>
              <span className="text-meta"> · {b.imported_count} imported{b.updated_count > 0 && `, ${b.updated_count} updated`}{b.skipped_count > 0 && `, ${b.skipped_count} skipped`} · {new Date(b.created_at).toLocaleString()}{b.created_by_name && ` · by ${b.created_by_name}`}</span>
            </div>
            {b.type === 'members' && b.undoable ? (
              <button onClick={() => onReview(b)} className="text-blue-600 hover:underline text-xs font-medium">Review &amp; undo</button>
            ) : b.reversed_at ? (
              <span className="text-xs text-meta">{b.reversed_by_name ? `Undone by ${b.reversed_by_name}` : 'Undone'}</span>
            ) : (
              /* Why undo is unavailable. The reason comes back on every batch
                 (batches.php), but this row used to drop it and show a bare "—",
                 and the screen that does explain is only reachable through the
                 "Review & undo" link above — which isn't rendered when a batch
                 can't be undone. Blocked batches were a dead end. */
              <span
                className="text-xs text-meta ml-4 shrink text-right truncate"
                title={b.undo_reason || undefined}
              >
                {b.undo_reason || '—'}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------- small helpers ----------
const Stat = ({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' | 'danger' }) => (
  <div className="text-center px-4 py-2 rounded-lg bg-surface-app">
    <div className={`text-2xl font-bold ${tone === 'danger' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-heading'}`}>{value}</div>
    <div className="text-xs text-meta">{label}</div>
  </div>
);
const IssueList = ({ title, items, tone }: { title: string; items: string[]; tone: 'danger' | 'warn' }) => (
  <div className={`rounded-lg border ${tone === 'danger' ? 'border-status-danger' : 'border-status-warning'} overflow-hidden`}>
    <div className={`px-3 py-2 text-sm font-medium flex items-center gap-2 ${tone === 'danger' ? 'bg-status-danger-subtle text-red-800' : 'bg-status-warning-subtle text-amber-800'}`}>
      {tone === 'danger' ? <XCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{title}
    </div>
    <ul className="max-h-56 overflow-y-auto text-sm divide-y divide-default">
      {items.map((it, i) => <li key={i} className="px-3 py-1.5 text-body">{it}</li>)}
    </ul>
  </div>
);
